import type { FC } from 'react';
import { useAppSelector } from '../../redux/hooks';
import { selectedUnit } from '../../redux/reducers/unitSlice';
import {
  resolveUnitBaseColor,
  unitHeaderBadgeColors,
} from '../../businessLogicService/StudentDashboardAux/getColorByUnit';
import { UnitHeaderFoundationsIcon } from './DashboardUnitHeaderIcons/UnitHeaderFoundationsIcon';
import { UnitHeaderSupportIcon } from './DashboardUnitHeaderIcons/UnitHeaderSupportIcon';
import { UnitHeaderTargetIcon } from './DashboardUnitHeaderIcons/UnitHeaderTargetIcon';

type BadgeVariant = 'foundations' | 'support' | 'target';

interface Props {
  variant: BadgeVariant;
  // Foundations: whether every foundation skill is complete. The badge is hidden until then.
  isActive?: boolean;
  // Support / Target: number of completed skills, overlaid on the badge's bubble.
  count?: number;
}

const badgeSizeClassName = 'tw-h-16 tw-w-auto';

// Renders one of the designed unit-header badge SVGs (DashboardUnitHeaderIcons/*), tinted with the
// selected unit's colors. Foundations only appears once complete; Support/Target are always shown —
// muted (grayscale + reduced opacity) while their count is 0 and in full unit color once above 0,
// with the real count overlaid on the badge's bubble.
export const UnitSkillBadge: FC<Props> = ({ variant, isActive, count }) => {
  const unit = useAppSelector(selectedUnit);
  const colors = unitHeaderBadgeColors[resolveUnitBaseColor(unit?.name)];

  if (variant === 'foundations') {
    if (!isActive) return null;
    return <UnitHeaderFoundationsIcon className={badgeSizeClassName} colors={colors} />;
  }

  const BadgeIcon = variant === 'support' ? UnitHeaderSupportIcon : UnitHeaderTargetIcon;
  const muted = (count ?? 0) === 0;

  // When muted, the container is only faded (opacity) so the central multicolor icon keeps its color;
  // the grayscale is applied to the unit-colored chrome (inside the SVG) and to the count text.
  return (
    <div className={`tw-relative tw-inline-flex ${muted ? 'tw-opacity-80' : ''}`}>
      <BadgeIcon className={badgeSizeClassName} colors={colors} muted={muted} />
      <span
        className={`tw-absolute tw-left-1/2 tw-top-[82%] tw-translate-x-[-50%] tw-translate-y-[-50%] tw-text-sm tw-font-bold tw-leading-none tw-font-['Atkinson_Hyperlegible'] ${
          colors.count
        } ${muted ? 'tw-grayscale' : ''}`}
      >
        {count}
      </span>
    </div>
  );
};
